import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import OrderService from '../services/OrderService';
import { isEmpty } from 'lodash';

const initialState = {
  data: [],
  datas: [],
  formError: [],
  pagination: {
    page: 0,
    per_page: 10,
    total: 10,
    totalPages: 10,
  },
  error: null,
  status: '',
  loading: false,
  submitLoading: false,
  isPendingOrderDetails: false,
  isOpenInventoryDialog: false,
  details: {},
  orderItems: [],

  loadItems: {
    loading: false,
    items: [],
    page: 1,
    hasNextPage: false,
    error: null,
    loadMore: false,
    shop_id: null,
  },
  order: {
    loading: false,
    isSuccess: false,
  },
  grand_total: 0,
  total_amount: 0,
  commission_amount: 0,
  sales_grand_total: 0,
  sales_total_amount: 0,
  sales_commission_amount: 0,
};

export const fetchOrderList = createAsyncThunk('order/all', async (payload) => {
  try {
    const resp = await OrderService.All(payload);
    return resp;
  } catch (e) {
    return e.response;
  }
});

export const fetchOrderDetails = createAsyncThunk(
  'order/fetchOrderDetails',
  async (payload, { rejectWithValue }) => {
    try {
      const id = typeof payload === 'object' ? payload.id : payload;
      const isSale = typeof payload === 'object' && payload.isSale === true;
      const resp = await OrderService.Details(id, isSale);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateOrderStatus = createAsyncThunk(
  'order/updateOrderStatuses',
  async (payload, { rejectWithValue }) => {
    try {
      const resp = await OrderService.UpdateStatues(payload);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateOrderItem = createAsyncThunk(
  'order/updateOrderItem',
  async (payload, { rejectWithValue }) => {
    try {
      const resp = await OrderService.UpdateOrderItem(payload);
      return resp;
    } catch (error) {
      return rejectWithValue({
        ...(error.response?.data || {}),
        status: error.response?.status,
      });
    }
  },
);

export const updateReceivedOrderItem = createAsyncThunk(
  'order/updateReceivedOrderItem',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const resp = await OrderService.UpdateReceivedOrderItem(id, payload);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const deleteOrderItem = createAsyncThunk(
  'order/deleteOrderItem',
  async (id, { rejectWithValue }) => {
    try {
      const resp = await OrderService.DeleteOrderItem(id);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const fetchItemListByAdmin = createAsyncThunk(
  'order/fetchItemListByAdmin',
  async (payload, { rejectWithValue }) => {
    try {
      const resp = await OrderService.fetchItemListByAdmin(payload);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const createOrder = createAsyncThunk(
  'order/createOrder',
  async (payload, { rejectWithValue }) => {
    try {
      const resp = await OrderService.createOrder(payload);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const cloneOrder = createAsyncThunk(
  'order/cloneOrder',
  async (id, { rejectWithValue }) => {
    try {
      const resp = await OrderService.cloneOrder(id);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const orderSlice = createSlice({
  name: 'order',
  initialState: initialState,
  reducers: {
    setShopId: (state, action) => {
      state.loadItems.shop_id = action.payload;
      state.loadItems.items = []; // <-- clear previous items
      state.loadItems.page = 1;
      state.loadItems.hasNextPage = false;
      state.loadItems.error = null;
    },
    resetOrderState: (state) => {
      state.order.isSuccess = false;
      state.order.loading = false;
    },
    setOrderDetails: (state, action) => {
      state.details = action.payload;
      state.orderItems = action.payload.items || [];
      state.isPendingOrderDetails = false;
      state.isOrderDetailsErr = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrderList.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(fetchOrderList.fulfilled, (state, action) => {
        if (action.payload?.status === 200) {
          state.datas = [...action.payload?.data?.data?.orderList];
          state.pagination = action.payload?.data?.data?.pagination;
          state.grand_total = action.payload?.data?.data?.grand_total || 0;
          state.total_amount = action.payload?.data?.data?.total_amount || 0;
          state.commission_amount = action.payload?.data?.data?.commission_amount || 0;
          state.sales_grand_total = action.payload?.data?.data?.sales_grand_total || 0;
          state.sales_total_amount = action.payload?.data?.data?.sales_total_amount || 0;
          state.sales_commission_amount = action.payload?.data?.data?.sales_commission_amount || 0;
        } else {
          state.error = action.payload?.data.message;
          toast.error(state.error);
        }
        state.loading = false;
      })
      .addCase(fetchOrderList.rejected, (state, action) => {
        state.error = action.payload?.data;
        state.loading = false;
      })
      .addCase(fetchOrderDetails.pending, (state, action) => {
        state.isPendingOrderDetails = true;
        state.isOrderDetailsErr = false;
      })
      .addCase(fetchOrderDetails.fulfilled, (state, action) => {
        state.isPendingOrderDetails = false;
        if (isEmpty(action.payload)) {
          state.isOrderDetailsErr = false;
        } else {
          state.details = action.payload.data.data;
          state.orderItems = action.payload.data.data.items || [];
        }
      })
      .addCase(fetchOrderDetails.rejected, (state, action) => {
        state.isPendingOrderDetails = false;
        state.isOrderDetailsErr = true;
      })
      .addCase(updateOrderStatus.pending, (state, action) => {
        state.submitLoading = true;
        state.isSuccess = false;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        const updatedOrders = action.payload.data.data;
        const updatedOrdersMap = new Map();
        updatedOrders.forEach((order) => {
          updatedOrdersMap.set(order.id, order.status);
        });

        state.datas = state.datas.map((order) => {
          if (updatedOrdersMap.has(order.id)) {
            return {
              ...order,
              status: updatedOrdersMap.get(order.id),
            };
          }
          return order;
        });

        state.isSuccess = true;
        state.submitLoading = false;
        toast.success('Order status updated successfully!');

        if (!isEmpty(state.details)) {
          const updated = action.payload.data.data[0]; // assuming single order update
          if (state.details && state.details.id === updated.id) {
            state.details.status = updated.status;
          }
        }
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.submitLoading = false;
        state.isSuccess = false;

        if (action.payload) {
          const errors = action.payload?.errors;
          const message = action.payload?.message || 'Something went wrong';

          if (errors && !isEmpty(errors)) {
            state.formError = errors;
          }
          toast.error(message);
        } else {
          toast.error(action.error.message || 'Failed to update order status');
        }
      })
      .addCase(updateOrderItem.pending, (state, action) => {
        state.submitLoading = true;
        state.isSuccess = false;
      })
      .addCase(updateOrderItem.fulfilled, (state, action) => {
        const orders = action.payload.data.data || [];
        const transitionMessages = [];

        for (const order of orders) {
          const matchesDetails =
            state.details?.id === order.order_id ||
            Number(state.details?.id) === Number(order.order_id);

          if (matchesDetails) {
            state.details.status = order.order_status;
            state.details.allow_backorder = order.allow_backorder;
          }

          // Update order status in the main list (state.datas)
          const dataIndex = state.datas.findIndex(
            (d) => d.id === order.order_id || Number(d.id) === Number(order.order_id),
          );
          if (dataIndex !== -1) {
            state.datas[dataIndex] = {
              ...state.datas[dataIndex],
              status: order.order_status,
              allow_backorder: order.allow_backorder,
            };
          }

          const transition = order.status_transition;
          if (transition?.changed_to_prepared) {
            transitionMessages.push('Order status changed to prepared.');
          } else if (transition?.reason) {
            transitionMessages.push(transition.reason);
          }

          for (const updatedItem of order.items || []) {
            const index = state.orderItems.findIndex((item) => item.id === updatedItem.id);
            if (index !== -1) {
              state.orderItems[index] = {
                ...state.orderItems[index],
                quantity_fulfilled: updatedItem.success
                  ? updatedItem.quantity_fulfilled
                  : state.orderItems[index].quantity_fulfilled,
                total_weight_fulfilled: updatedItem.success
                  ? updatedItem.total_weight_fulfilled
                  : state.orderItems[index].total_weight_fulfilled,
                fulfillment_quantity: updatedItem.success
                  ? updatedItem.fulfillment_quantity
                  : state.orderItems[index].fulfillment_quantity,
                fulfillment_uom_id: updatedItem.success
                  ? updatedItem.fulfillment_uom_id
                  : state.orderItems[index].fulfillment_uom_id,
                fulfillment_conversion_definition_id: updatedItem.success
                  ? updatedItem.fulfillment_conversion_definition_id
                  : state.orderItems[index].fulfillment_conversion_definition_id,
                fulfillment_conversion_version: updatedItem.success
                  ? updatedItem.fulfillment_conversion_version
                  : state.orderItems[index].fulfillment_conversion_version,
                fulfillment_conversion_factor: updatedItem.success
                  ? updatedItem.fulfillment_conversion_factor
                  : state.orderItems[index].fulfillment_conversion_factor,
                status: updatedItem.status,
              };
              if (state.details?.items?.[index]) {
                state.details.items[index] = {
                  ...state.orderItems[index],
                  quantity_fulfilled: updatedItem.success
                    ? updatedItem.quantity_fulfilled
                    : state.orderItems[index].quantity_fulfilled,
                  total_weight_fulfilled: updatedItem.success
                    ? updatedItem.total_weight_fulfilled
                    : state.orderItems[index].total_weight_fulfilled,
                  fulfillment_quantity: updatedItem.success
                    ? updatedItem.fulfillment_quantity
                    : state.orderItems[index].fulfillment_quantity,
                  fulfillment_uom_id: updatedItem.success
                    ? updatedItem.fulfillment_uom_id
                    : state.orderItems[index].fulfillment_uom_id,
                  fulfillment_conversion_definition_id: updatedItem.success
                    ? updatedItem.fulfillment_conversion_definition_id
                    : state.orderItems[index].fulfillment_conversion_definition_id,
                  fulfillment_conversion_version: updatedItem.success
                    ? updatedItem.fulfillment_conversion_version
                    : state.orderItems[index].fulfillment_conversion_version,
                  fulfillment_conversion_factor: updatedItem.success
                    ? updatedItem.fulfillment_conversion_factor
                    : state.orderItems[index].fulfillment_conversion_factor,
                  status: updatedItem.status,
                };
              }
            }
          }
        }

        state.isSuccess = true;
        state.submitLoading = false;
        toast.success('Order item(s) updated successfully!');
        if (transitionMessages.length > 0) {
          const stayedConfirmed = orders.some(
            (o) => o.order_status === 'confirmed' && o.status_transition?.changed_to_prepared === false,
          );
          if (stayedConfirmed) {
            toast.info(transitionMessages.join(' '));
          }
        }
      })
      .addCase(updateOrderItem.rejected, (state, action) => {
        state.submitLoading = false;
        state.isSuccess = false;

        if (action.payload) {
          const errors = action.payload?.errors;
          const message = action.payload?.message || 'Something went wrong';

          if (errors && !isEmpty(errors)) {
            state.formError = errors;
          }
          toast.error(message);
        } else {
          toast.error(action.error.message || 'Failed to update order item');
        }
      })
      .addCase(updateReceivedOrderItem.pending, (state) => {
        state.submitLoading = true;
        state.isSuccess = false;
      })
      .addCase(updateReceivedOrderItem.fulfilled, (state, action) => {
        state.submitLoading = false;
        state.isSuccess = true;
        toast.success(action.payload?.data?.message || 'Order item updated successfully!');
      })
      .addCase(updateReceivedOrderItem.rejected, (state, action) => {
        state.submitLoading = false;
        state.isSuccess = false;
        toast.error(action.payload?.message || action.error?.message || 'Failed to update order item');
      })
      .addCase(deleteOrderItem.pending, (state) => {
        state.submitLoading = true;
        state.isSuccess = false;
      })
      .addCase(deleteOrderItem.fulfilled, (state, action) => {
        state.submitLoading = false;
        state.isSuccess = true;
        toast.success(action.payload?.data?.message || 'Order item deleted successfully!');
      })
      .addCase(deleteOrderItem.rejected, (state, action) => {
        state.submitLoading = false;
        state.isSuccess = false;
        toast.error(action.payload?.message || action.error?.message || 'Failed to delete order item');
      })
      .addCase(fetchItemListByAdmin.pending, (state, action) => {
        state.loadItems.loading = true;
        state.loadItems.error = null;

        if (action.meta.arg.page === 1) {
          state.loadItems.items = [];
        }
      })
      .addCase(fetchItemListByAdmin.fulfilled, (state, action) => {
        if (action.payload?.status === 200) {
          const newList = action.payload?.data?.data?.items || [];
          const page = action.meta.arg.page;
          const isLoadMore = action.meta.arg.loadMore;

          if (page === 1) {
            state.loadItems.items = newList;
          } else {
            state.loadItems.items = [...state.loadItems.items, ...newList];
          }
          state.loadItems.page = page;
          state.loadItems.hasNextPage = newList.length >= (action.meta.arg.perPage || 30);
          state.loadItems.loading = false;
        }
      })
      .addCase(fetchItemListByAdmin.rejected, (state, action) => {
        state.loadItems.error = action.payload?.data || 'Failed to load items';
        state.loadItems.loading = false;
        state.loadItems.loadMore = false;
        toast.error(state.loadItems.error);
      })
      .addCase(createOrder.pending, (state, action) => {
        state.order.loading = true;
        state.order.isSuccess = false;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.order.loading = false;
        state.order.isSuccess = true;
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.order.loading = false;
        state.order.isSuccess = false;
        toast.error(action.payload?.message);
      })
      .addCase(cloneOrder.pending, (state) => {
        state.submitLoading = true;
      })
      .addCase(cloneOrder.fulfilled, (state, action) => {
        state.submitLoading = false;
        toast.success(action.payload?.data?.message || 'Order cloned successfully!');
      })
      .addCase(cloneOrder.rejected, (state, action) => {
        state.submitLoading = false;
        toast.error(action.payload?.message || 'Failed to clone order');
      })
      .addCase(addItemToOrder.pending, (state, action) => {
        state.submitLoading = true;
        state.isSuccess = false;
      })
      .addCase(addItemToOrder.fulfilled, (state, action) => {
        state.submitLoading = false;
        if (action.payload?.status === 200 || action.payload?.status === 201) {
          const newItem = action.payload.data.data;
          state.orderItems = [...state.orderItems, newItem];
          if (state.details && state.details.items) {
            state.details.items = [...state.details.items, newItem];
          }
          state.isSuccess = true;
          toast.success('Item added to order successfully!');
        } else {
          toast.error(action.payload?.data?.message || 'Failed to add item');
        }
      })
      .addCase(addItemToOrder.rejected, (state, action) => {
        state.submitLoading = false;
        state.isSuccess = false;
        toast.error(action.payload?.message || 'Failed to add item');
      });
  },
});

export const addItemToOrder = createAsyncThunk(
  'order/addItemToOrder',
  async ({ orderId, payload }, { rejectWithValue }) => {
    try {
      const resp = await OrderService.addItemToOrder(orderId, payload);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const { reducer } = orderSlice;

export const { setShopId, resetOrderState, setOrderDetails } = orderSlice.actions;

export const orderSelector = (state) => state?.order;

export default reducer;
